// src/pages/admin/AdminEducationScorer.jsx

import React, { useEffect, useState } from 'react';
import { matchingModelAPI } from '../../services/api';
import { Spinner } from '../../components/shared/UI';
import toast from 'react-hot-toast';
import { Save, RotateCcw, Layers, GraduationCap } from 'lucide-react';

const DEFAULTS = {
  grades: {
    grade1: { enabled: true,  weight: 40 },
    grade2: { enabled: true,  weight: 60 },
  },
  educationLevelBoost: {
    enabled: true,
    maxBoostPercent: 20,
    proximityScores: [100, 85, 65, 45, 25, 10, 0, 0],
    // index 0 = exact match, 1 = 1 level apart, ... 7 = max gap
  },
};

const GRADE_INFO = [
  { key: 'grade1', label: 'Grade 1 — Collated (All)',
    desc: 'All seeker education fields of study are collated and compared to the collated fields of study required by the position.' },
  { key: 'grade2', label: 'Grade 2 — Individual',
    desc: 'Each seeker education field of study is compared individually to each position education field of study.' },
];

const EDUCATION_LEVELS = [
  'High School Certificate', 'Diploma', 'Advanced Diploma', 'Graduate Certificate',
  'Graduate Diploma', 'Bachelor Degree', 'Master Degree', 'Doctorate',
];

/* ── Toggle switch ───────────────────────────────────────── */
const Toggle = ({ checked, onChange }) => (
  <label style={{ position: 'relative', display: 'inline-block', width: 42, height: 24, flexShrink: 0 }}>
    <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
      style={{ opacity: 0, width: 0, height: 0 }} />
    <span style={{
      position: 'absolute', cursor: 'pointer', inset: 0, borderRadius: 12,
      background: checked ? '#28A745' : '#ccc', transition: 'background 0.2s',
    }}>
      <span style={{
        position: 'absolute', height: 18, width: 18, left: checked ? 20 : 4, top: 3,
        background: '#fff', borderRadius: '50%', transition: 'left 0.2s',
      }} />
    </span>
  </label>
);

/* ── Grade row ───────────────────────────────────────────── */
const GradeRow = ({ info, grade, onChange }) => (
  <div style={{ padding: '14px 0' }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Toggle checked={grade.enabled} onChange={v => onChange({ enabled: v })} />
          <label className="form-label" style={{ marginBottom: 0 }}>{info.label}</label>
        </div>
        <div style={{ fontSize: 12, color: '#888', marginTop: 4, paddingLeft: 54 }}>{info.desc}</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, opacity: grade.enabled ? 1 : 0.4 }}>
        <span style={{ fontSize: 12, color: '#888', whiteSpace: 'nowrap' }}>Weight</span>
        <input
          type="number" min="0" max="100" value={grade.weight} disabled={!grade.enabled}
          onChange={e => onChange({ weight: Math.max(0, Math.min(100, parseInt(e.target.value) || 0)) })}
          className="form-input"
          style={{ width: 80, textAlign: 'center', fontFamily: 'monospace', fontWeight: 700 }}
        />
      </div>
    </div>
  </div>
);

/* ── Score row ───────────────────────────────────────────── */
const ScoreRow = ({ label, value, onChange, suffix, description, max }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '10px 0' }}>
    <div style={{ flex: 1 }}>
      <label className="form-label" style={{ marginBottom: 0 }}>{label}</label>
      {description && <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{description}</div>}
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <input
        type="number" min="0" max={max || 999} value={value}
        onChange={e => onChange(Math.max(0, Math.min(max || 999, parseInt(e.target.value) || 0)))}
        className="form-input"
        style={{ width: 80, textAlign: 'center', fontFamily: 'monospace', fontWeight: 700 }}
      />
      <span style={{ fontSize: 12, color: '#888', minWidth: 40 }}>{suffix || ''}</span>
    </div>
  </div>
);

/* ── Main page ───────────────────────────────────────────── */
export const AdminEducationScorer = () => {
  const [config, setConfig] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const mergeConfig = (d) => ({
    grades: {
      grade1: { ...DEFAULTS.grades.grade1, ...d.grades?.grade1 },
      grade2: { ...DEFAULTS.grades.grade2, ...d.grades?.grade2 },
    },
    educationLevelBoost: {
      ...DEFAULTS.educationLevelBoost,
      ...d.educationLevelBoost,
      proximityScores: Array.isArray(d.educationLevelBoost?.proximityScores) && d.educationLevelBoost.proximityScores.length === 8
        ? d.educationLevelBoost.proximityScores
        : DEFAULTS.educationLevelBoost.proximityScores,
    },
  });

  useEffect(() => {
    matchingModelAPI.getEducationConfig()
      .then(({ data }) => { if (data.data) setConfig(mergeConfig(data.data)); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await matchingModelAPI.updateEducationConfig(config);
      setConfig(mergeConfig(data.data));
      toast.success('Education scorer config saved');
    } catch { /* interceptor toasts */ }
    finally { setSaving(false); }
  };

  const handleReset = () => {
    setConfig(DEFAULTS);
    toast.success('Reset to defaults');
  };

  const updateGrade = (key) => (patch) =>
    setConfig(c => ({ ...c, grades: { ...c.grades, [key]: { ...c.grades[key], ...patch } } }));

  const updateBoost = (patch) =>
    setConfig(c => ({ ...c, educationLevelBoost: { ...c.educationLevelBoost, ...patch } }));

  const updateProximityScore = (index, value) =>
    setConfig(c => {
      const scores = [...c.educationLevelBoost.proximityScores];
      scores[index] = value;
      return { ...c, educationLevelBoost: { ...c.educationLevelBoost, proximityScores: scores } };
    });

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 64 }}><Spinner size={32} /></div>;

  const enabledGrades = GRADE_INFO.filter(g => config.grades[g.key].enabled);
  const totalWeight = enabledGrades.reduce((sum, g) => sum + config.grades[g.key].weight, 0);

  return (
    <div>
      <p style={{ color: '#666', fontSize: 13, marginBottom: 20 }}>
        Configure semantic matching between seeker education fields of study and position education requirements.
        Cosine similarity is applied at different granularity levels.
      </p>

      {/* ── Granularity Grades ── */}
      <div className="section-box" style={{ marginBottom: 20 }}>
        <div className="section-box-header" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Layers size={18} />
          <span>Granularity Grades</span>
        </div>
        <div className="section-box-body" style={{ padding: 20 }}>
          {GRADE_INFO.map((info, i) => (
            <React.Fragment key={info.key}>
              {i > 0 && <div style={{ borderTop: '1px solid #eee' }} />}
              <GradeRow info={info} grade={config.grades[info.key]} onChange={updateGrade(info.key)} />
            </React.Fragment>
          ))}

          <div style={{ borderTop: '1px solid #ddd', marginTop: 8, paddingTop: 12,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: '#666' }}>
              {enabledGrades.length} of 2 grades enabled
            </span>
            <span style={{ fontSize: 13, fontWeight: 700, color: totalWeight === 100 ? '#28A745' : '#CC0000' }}>
              Total weight: {totalWeight} / 100
            </span>
          </div>
          {totalWeight !== 100 && (
            <div style={{ fontSize: 12, color: '#CC0000', textAlign: 'right', marginTop: 4 }}>
              Weights of enabled grades should sum to 100. They will be normalised at scoring time.
            </div>
          )}
        </div>
      </div>

      {/* ── Education Level Proximity Boost ── */}
      <div className="section-box" style={{ marginBottom: 20 }}>
        <div className="section-box-header" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <GraduationCap size={18} />
          <span>Education Level Proximity Boost</span>
        </div>
        <div className="section-box-body" style={{ padding: 20 }}>
          <p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
            When the seeker's qualification level is close to the position's required level, the match score is boosted.
            The ordinal distance is measured across 8 levels: {EDUCATION_LEVELS.join(' → ')}.
          </p>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <Toggle checked={config.educationLevelBoost.enabled} onChange={v => updateBoost({ enabled: v })} />
            <label className="form-label" style={{ marginBottom: 0 }}>Enable Education Level Boost</label>
          </div>

          <div style={{ opacity: config.educationLevelBoost.enabled ? 1 : 0.4 }}>
            <ScoreRow label="Max Boost" value={config.educationLevelBoost.maxBoostPercent}
              onChange={v => updateBoost({ maxBoostPercent: Math.min(100, v) })} suffix="%" max={100}
              description="Maximum percentage increase to the similarity score from education level proximity." />
            <div style={{ borderTop: '1px solid #eee', marginBottom: 4 }} />

            <div style={{ padding: '6px 0 2px' }}>
              <label className="form-label" style={{ marginBottom: 4 }}>Proximity Scores</label>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>
                Score for each ordinal distance between seeker and position education levels (0 = exact match, 7 = maximum gap).
              </div>
            </div>
            {config.educationLevelBoost.proximityScores.map((score, i) => (
              <React.Fragment key={i}>
                {i > 0 && <div style={{ borderTop: '1px solid #f5f5f5' }} />}
                <ScoreRow
                  label={i === 0 ? 'Exact match' : `${i} level${i > 1 ? 's' : ''} apart`}
                  value={score}
                  onChange={v => updateProximityScore(i, Math.min(100, v))}
                  suffix="/ 100" max={100}
                />
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* ── Actions ── */}
      <div style={{ display: 'flex', gap: 12 }}>
        <button onClick={handleReset} className="btn btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <RotateCcw size={15} /><span>Reset Defaults</span>
        </button>
        <button onClick={handleSave} disabled={saving}
          className="btn btn-success" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          {saving ? <Spinner size={16} /> : <><Save size={15} /><span>Save Configuration</span></>}
        </button>
      </div>
    </div>
  );
};
